import { StatorParams, WindingType, CoilShape } from './pcb-stator-geometry';
import { TerminalStyle } from './pcb-stator-terminals';
import { MountingScrew } from './pcb-stator-mounting';
import { MagnetShape } from './pcb-stator-magnets';

/** Everything needed to reproduce a stator configuration. Inputs only — never view state. */
export interface StatorInputs {
  params: StatorParams;
  windingType: WindingType;
  coilShape: CoilShape;
  terminalStyle: TerminalStyle;
  /** 'adjacent' | 'stacking-tabs' — optional so a config saved before this field
   *  existed still parses (loadAll's schemaVersion filter accepts it as-is);
   *  `applyConfig` defaults a missing value to 'adjacent'. */
  terminalLayout?: 'adjacent' | 'stacking-tabs';
  mountingHoles: number;
  mountingScrew: MountingScrew;
  mountingHoleDiaMm: number | null;
  mountingBoltRadius: number;
  deltaTempC: number;
  airGapT: number;
  genRpm: number;
  rotorMagnetShape: MagnetShape;
  rotorMagnetThicknessMm: number;
  rotorDiscThicknessMm: number;
  rotorBoreDiameterMm: number;
  rotorPocketClearanceMm: number;
}

/** Denormalized snapshot of headline calculated values, captured at save time. */
export interface HeadlineStats {
  boardDiameterMm: number;
  resistancePerPhaseOhm: number;
  lineVoltageRms: number;
}

export interface SavedStatorConfig {
  id: string;
  name: string;
  createdAt: number;
  schemaVersion: 1;
  inputs: StatorInputs;
  headlineStats: HeadlineStats;
}

export const STORAGE_KEY = 'pcb-stator-saved-configs';
const SCHEMA_VERSION = 1;

export function loadAll(): SavedStatorConfig[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (c): c is SavedStatorConfig => !!c && c.schemaVersion === SCHEMA_VERSION,
    );
  } catch {
    return [];
  }
}

/** Returns true if the write succeeded; false (with a warning) if storage is full/unavailable. */
export function saveAll(list: SavedStatorConfig[]): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return true;
  } catch (e) {
    console.warn('pcb-stator: could not persist saved configs', e);
    return false;
  }
}

export function addConfig(list: SavedStatorConfig[], config: SavedStatorConfig): SavedStatorConfig[] {
  return [...list, config];
}

export function deleteConfig(list: SavedStatorConfig[], id: string): SavedStatorConfig[] {
  return list.filter(c => c.id !== id);
}

export function renameConfig(list: SavedStatorConfig[], id: string, name: string): SavedStatorConfig[] {
  return list.map(c => (c.id === id ? { ...c, name } : c));
}

export function makeId(): string {
  return `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}
